import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";
import ContratoAutocomplete from "../../../components/autocomplete/ContratoAutocomplete";
import ProductoAutocomplete from "../../../components/autocomplete/ProductoAutocomplete";
import InputNumberNoScroll from "../../../components/inputs/inputNumberNoScroll";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson, notify } from "../../../utils/http";

export default function CrearProductoContrato({
  onClose = () => {},
  onCreated = () => {},
  embedded = false,
  contratoPredefinido = null,
  initialData = null,
}) {
  const formatNumber = (value) => {
    if (!value && value !== 0) return "";
    const digits = value.toString().replace(/\D/g, "");
    if (!digits) return "";
    return Number(digits).toLocaleString("es-PY");
  };

  const cleanNumber = (val) => {
    if (!val && val !== 0) return null;
    const digits = val.toString().replace(/\D/g, "");
    return digits ? Number(digits) : null;
  };

  // --- Estados ---
  const [contratoObj, setContratoObj] = useState(
    initialData
      ? { id: initialData.contratoId, codigo: initialData.contratoCodigo }
      : contratoPredefinido || null,
  );
  const [productoObj, setProductoObj] = useState(
    initialData
      ? {
          id: initialData.productoId,
          nombre: initialData.productoNombre,
          codigo: initialData.productoCodigo,
        }
      : null,
  );
  const [qnProducto, setQnProducto] = useState(
    initialData?.qnProducto?.toString() || "",
  );
  const [valorMontoDescuento, setValorMontoDescuento] = useState(
    initialData?.valorMontoDescuento?.toString() || "",
  );
  const [valorMonto, setValorMonto] = useState(
    initialData?.valorMonto?.toString() || "",
  );

  // Campos de solo lectura: para edición se toman de initialData, para creación se fijan en 0
  const [qnUtilizado, setQnUtilizado] = useState(
    initialData ? initialData.qnUtilizado : 0,
  );
  const [valorMontoUtilizado, setValorMontoUtilizado] = useState(
    initialData ? initialData.valorMontoUtilizado : 0,
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Manejo de cambios con formato
  const handleQnChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    setQnProducto(digits);
  };
  const handleDescuentoChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    setValorMontoDescuento(digits);
  };
  const handleMontoChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    setValorMonto(digits);
  };

  // Validaciones completas
  const qn = cleanNumber(qnProducto);
  const desc = cleanNumber(valorMontoDescuento);
  const monto = cleanNumber(valorMonto);

  const tieneProducto = qn > 0 && desc > 0;
  const tieneMonto = monto > 0 && !qn && !desc;
  const esValido =
    (tieneProducto || tieneMonto) &&
    !(tieneProducto && monto > 0) && // no ambos tipos
    !(qn > 0 && !desc) && // cantidad sin descuento
    !(desc > 0 && !qn); // descuento sin cantidad

  const isValid = contratoObj && productoObj && (qn || desc || monto);

  // Cerrar con ESC
  useEffect(() => {
    if (embedded) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, embedded]);

  const handleSave = async () => {
    if (!isValid || !esValido || saving) return;

    setSaving(true);
    setError("");

    const existingId = initialData?.id;

    const payload = {
      contratoId: contratoObj.id,
      productoId: productoObj.id,
      qnProducto: qn,
      valorMontoDescuento: desc,
      valorMonto: monto,
    };

    // Solo se envían los campos utilizados si es creación (para no sobrescribir en edición)
    if (!existingId) {
      payload.qnUtilizado = 0;
      payload.valorMontoUtilizado = 0;
    }

    try {
      let result;
      if (existingId) {
        const url = apiRoutes.contratoDetalle.editarUrl(existingId);
        result = await appFetchJson(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        notify({
          type: "success",
          message: "Producto actualizado correctamente",
        });
      } else {
        result = await appFetchJson(apiRoutes.contratoDetalle.agregarUrl(), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        notify({ type: "success", message: "Producto agregado correctamente" });
      }
      onCreated(result);
      if (!embedded) onClose();
      else {
        // Limpiar solo si es creación y no hay contrato predefinido
        if (!existingId && !contratoPredefinido) {
          setContratoObj(null);
          setProductoObj(null);
          setQnProducto("");
          setValorMontoDescuento("");
          setValorMonto("");
          setQnUtilizado(0);
          setValorMontoUtilizado(0);
        }
      }
    } catch (e) {
      console.error("Error guardando producto en contrato:", e);
      const errorMsg = e?.message || "Error al guardar el producto.";
      setError(errorMsg);
      notify({ type: "error", message: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <>
      {!embedded && (
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">Agregar Producto al Contrato</h2>
          <button className="modal-close-x" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
      )}

      <div className={embedded ? "" : "modal-body"}>
        {!embedded && (
          <p className="text-gray-600 mb-4 italic">
            Complete los datos para asociar un producto a un contrato.
          </p>
        )}

        <div className="form-grid">
          {/* Contrato */}
          <div className="form-row col-span-2">
            <label className="form-label">Contrato *</label>
            <input
              type="text"
              className="input w-full bg-gray-100 cursor-not-allowed"
              value={contratoObj?.codigo || ""}
              disabled
              readOnly
            />
          </div>

          {/* Producto */}
          <div className="form-row col-span-2">
            <label className="form-label">Producto *</label>
            <div className="w-full">
              <ProductoAutocomplete
                value={productoObj}
                onChange={setProductoObj}
                placeholder="Buscar por código o nombre..."
              />
            </div>
          </div>

          <div className="col-span-2 border-t border-gray-200 my-2"></div>

          {/* Campos mutuamente excluyentes */}
          <div className="form-row col-span-2">
            <label className="form-label">Cantidad de Producto</label>
            <input
              type="text"
              className="input w-full"
              value={formatNumber(qnProducto)}
              onChange={handleQnChange}
              placeholder="Ej: 100"
              inputMode="numeric"
              onWheel={(e) => e.target.blur()}
              onKeyDown={(e) => {
                if (e.key === "ArrowUp" || e.key === "ArrowDown")
                  e.preventDefault();
              }}
            />
          </div>

          <div className="form-row col-span-2">
            <label className="form-label">Valor de Descuento Producto</label>
            <input
              type="text"
              className="input w-full"
              value={formatNumber(valorMontoDescuento)}
              onChange={handleDescuentoChange}
              placeholder="Ej: 5000"
              inputMode="numeric"
              onWheel={(e) => e.target.blur()}
              onKeyDown={(e) => {
                if (e.key === "ArrowUp" || e.key === "ArrowDown")
                  e.preventDefault();
              }}
            />
          </div>

          <div className="form-row col-span-2">
            <label className="form-label">Valor de Monto PYG</label>
            <input
              type="text"
              className="input w-full"
              value={formatNumber(valorMonto)}
              onChange={handleMontoChange}
              placeholder="Ej: 1.500.000"
              inputMode="numeric"
              onWheel={(e) => e.target.blur()}
              onKeyDown={(e) => {
                if (e.key === "ArrowUp" || e.key === "ArrowDown")
                  e.preventDefault();
              }}
            />
          </div>

          <div className="col-span-2 border-t border-gray-200 my-2"></div>

          {/* Campos solo lectura */}
          <div className="form-row col-span-2">
            <label className="form-label">Cantidad Producto Utilizado</label>
            <input
              type="text"
              className="input w-full bg-gray-100 cursor-not-allowed"
              value={formatNumber(qnUtilizado)}
              disabled
              readOnly
            />
          </div>

          <div className="form-row col-span-2">
            <label className="form-label">Valor de Monto PYG Utilizado</label>
            <input
              type="text"
              className="input w-full bg-gray-100 cursor-not-allowed"
              value={formatNumber(valorMontoUtilizado)}
              disabled
              readOnly
            />
          </div>

          {!esValido && (qn || desc || monto) && (
            <div className="col-span-2 text-red-500 text-sm mt-1">
              {qn > 0 &&
                !desc &&
                "El campo VALOR DE DESCUENTO DEL PRODUCTO no puede ser NULO."}
              {desc > 0 &&
                !qn &&
                "El campo CANTIDAD DE PRODUCTO NO puede ser NULO."}
              {qn > 0 &&
                desc > 0 &&
                monto > 0 &&
                "Solo puede cargar (Cantidad + Descuento) o (Valor Monto), no ambos."}
              {!qn &&
                !desc &&
                !monto &&
                "Debe cargar CANTIDAD DE PRODUCTO con su VALOR DE DESCUENTO O VALOR DE MONTO PYG."}
            </div>
          )}
        </div>

        {error && <div className="text-red-600 mt-3 font-medium">{error}</div>}
      </div>

      <div className={embedded ? "mt-6 border-b pb-6" : "modal-footer"}>
        <div className="btn-row">
          <BtnGuardar
            label={
              saving
                ? "Guardando..."
                : embedded
                  ? "Guardar y continuar"
                  : "Guardar"
            }
            isValid={isValid && esValido}
            saving={saving}
            onClick={handleSave}
          />
          {!embedded && <BtnCancelar onClick={onClose} />}
        </div>
      </div>
    </>
  );

  if (embedded) {
    return <div className="bg-white p-1">{content}</div>;
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        {content}
      </div>
    </div>
  );
}
